import { ConsequenceNote, SkeletonSet } from '@cwos/ui-admin';
import { SettingsActions, SettingsCard } from '../settings/common.js';
import {
  CLOCK_REQUIRED_HONESTY,
  EXCEPTION_APPROVAL_LABEL,
  EXCEPTION_APPROVAL_OFF_NOTE,
  EXCEPTION_APPROVAL_ON_NOTE,
  EXCEPTION_INTEGRITY_SENTENCE,
  EXCEPTION_REASON_LABEL,
  EXCEPTION_REASON_ON_NOTE,
  MODE_OFF_TRACKING_NOTE,
  ORGANIZATION_AUDIENCE,
} from './copy.js';
import { LocationSettingsShell } from './LocationSettingsShell.js';
import { PolicySchedule } from './PolicySchedule.js';
import { usePolicyForm } from './policyForm.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * Clock rules — what happens AROUND a punch that records a location exception
 * (design doc §6; the L117 slice). Both controls are real `location_policy`
 * columns (`exception_reason_required`, `exception_approval_required`), wired
 * through the resolver, the clock path, and the review queue — no decorative
 * toggles (§32). The page leads with the integrity rule the whole vertical is
 * built on, because it is the one promise every other sentence here relies on.
 */
export function LocationClockRulesPage() {
  const form = usePolicyForm(null);

  return (
    <LocationSettingsShell active="clock-rules" dirty={form.dirty}>
      {form.loading ? (
        <SettingsCard>
          <SkeletonSet surface="table" rows={4} />
        </SettingsCard>
      ) : form.loadError || !form.draft || !form.policy ? (
        <SettingsCard>
          <LoadFailure
            error={form.loadFailure}
            message="We couldn’t load the policy just now."
            onRetry={form.refetch}
          />
        </SettingsCard>
      ) : (
        <>
          {form.draft.mode === 'OFF' ? (
            <SettingsCard>
              <p className="rn-muted" role="status">
                {MODE_OFF_TRACKING_NOTE}
              </p>
            </SettingsCard>
          ) : null}

          <SettingsCard>
            <h2 className="rn-card-title">When a punch records an exception</h2>
            <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
              {EXCEPTION_INTEGRITY_SENTENCE}
            </p>
            <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-10)' }}>
              <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={form.draft.exceptionReasonRequired}
                  disabled={!form.canManage}
                  onChange={(e) => form.set({ exceptionReasonRequired: e.target.checked })}
                />
                <span>{EXCEPTION_REASON_LABEL}</span>
              </label>
              {form.draft.exceptionReasonRequired ? (
                <ConsequenceNote text={EXCEPTION_REASON_ON_NOTE} />
              ) : null}

              <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={form.draft.exceptionApprovalRequired}
                  disabled={!form.canManage}
                  onChange={(e) => form.set({ exceptionApprovalRequired: e.target.checked })}
                />
                <span>{EXCEPTION_APPROVAL_LABEL}</span>
              </label>
              {form.draft.exceptionApprovalRequired ? (
                <ConsequenceNote text={EXCEPTION_APPROVAL_ON_NOTE} />
              ) : (
                <p className="rn-muted" style={{ fontSize: 13 }}>
                  {EXCEPTION_APPROVAL_OFF_NOTE}
                </p>
              )}

              <p className="rn-muted" style={{ fontSize: 13 }}>
                {CLOCK_REQUIRED_HONESTY}
              </p>
            </div>
          </SettingsCard>

          {/* Every page that saves states when the save begins — see
              PolicySchedule's header: this page sends the whole policy body,
              so it makes a schedule decision on every save whether it shows
              one or not. */}
          <SettingsCard>
            <PolicySchedule form={form} audience={ORGANIZATION_AUDIENCE} />
          </SettingsCard>

          {form.canManage ? (
            <SettingsCard>
              <SettingsActions
                dirty={form.dirty}
                valid={form.valid}
                saving={form.saving}
                saved={form.saved}
                error={form.saveError}
                onSave={form.save}
                onCancel={form.cancel}
              />
            </SettingsCard>
          ) : null}
        </>
      )}
    </LocationSettingsShell>
  );
}
